"use client";

import { useEffect, useState } from "react";

/**
 * A sequence of steps on a clock, for a product moment's motion: the
 * current step, advancing after each step's duration and wrapping to the
 * start when `loop` is set. While `run` is false it waits on its first
 * step, for a moment that starts when it scrolls into view. With reduced
 * motion it rests on `restStep` (the last by default) and never moves, so a
 * moment is still readable.
 */
export function useTimeline(
  durations: readonly number[],
  { loop = true, restStep = durations.length - 1, run = true }: { loop?: boolean; restStep?: number; run?: boolean } = {},
): number {
  const [step, setStep] = useState(0);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (reduced || !run) return;
    const timer = window.setTimeout(() => {
      setStep((current) => (current + 1 < durations.length ? current + 1 : loop ? 0 : current));
    }, durations[step] ?? 1000);
    return () => window.clearTimeout(timer);
  }, [step, reduced, run, durations, loop]);

  return reduced ? restStep : step;
}
